import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Fingerprint, Loader2, Trash2, Eye, EyeOff, Save } from "lucide-react";
import { api } from "@/lib/api";
import { encryptText, decryptBlob } from "@/lib/crypto/cipherlock";
import { useRealtime } from "@/context/RealtimeContext";

/** Open the sealed trust word with the DM session key; null while the key isn't on this device. */
export function useLiveproof(chatId, userId, enabled = true) {
  const qc = useQueryClient();
  const { subscribe } = useRealtime();
  const q = useQuery({ queryKey: ["liveproof", chatId], queryFn: () => api.liveproofGet(chatId), enabled: !!chatId && enabled, retry: false });
  const [word, setWord] = useState(undefined); // undefined=loading, null=none/locked, string=open
  useEffect(() => {
    let cancelled = false;
    const blob = q.data?.word;
    if (!q.data) { setWord(undefined); return undefined; }
    if (!blob) { setWord(null); return undefined; }
    decryptBlob(userId, blob).then((pt) => { if (!cancelled) setWord(pt); });
    return () => { cancelled = true; };
  }, [q.data, userId]);
  useEffect(() => subscribe((evt) => {
    if ((evt?.type === "liveproof.set" || evt?.type === "liveproof.cleared") && (!evt.chat_id || evt.chat_id === chatId)) qc.invalidateQueries({ queryKey: ["liveproof", chatId] });
  }), [subscribe, chatId, qc]);
  return { word, meta: q.data, isLoading: q.isLoading, locked: !!q.data?.word && word === null };
}

// §2.10 Liveproof trust word — a per-relationship secret sealed client-side under the DM's Cipher Lock
// session key (server-blind). Ask for it on a call / before sending crypto to defeat deepfakes.
export function LiveproofSection({ chatId, userId, sessionId, peerName }) {
  const qc = useQueryClient();
  const { word, meta, isLoading, locked } = useLiveproof(chatId, userId);
  const [draft, setDraft] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const setMut = useMutation({
    mutationFn: (w) => {
      const blob = encryptText(sessionId, w.trim());
      if (!blob) throw new Error("no_key");
      return api.liveproofSet(chatId, blob);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["liveproof", chatId] }); setEditing(false); setDraft(""); toast.success("Trust word sealed — only the two of you can read it"); },
    onError: (e) => toast.error(e?.message === "no_key" ? "Cipher Lock key isn't ready on this device yet." : "Couldn't save the trust word."),
  });
  const clearMut = useMutation({
    mutationFn: () => api.liveproofClear(chatId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["liveproof", chatId] }); toast.success("Trust word cleared"); },
    onError: () => toast.error("Couldn't clear the trust word."),
  });
  const has = !!meta?.word;
  return (
    <div className="rounded-xl border border-white/10 bg-bloq-s2 p-3.5" data-testid="liveproof-section">
      <div className="flex items-center gap-2 text-sm font-semibold"><Fingerprint className="h-4 w-4 text-bloq-blue" /> Liveproof trust word</div>
      <p className="mt-1 text-xs text-bloq-muted">A secret only you and {peerName || "your contact"} know. Ask for it on a call or before sending crypto — deepfakes can't answer.</p>
      {isLoading ? (
        <div className="mt-3 flex justify-center text-bloq-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : has && !editing ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 rounded-xl border border-bloq-blue/30 bg-bloq-blue/10 px-3 py-2" data-testid="liveproof-word-row">
            <span className="flex-1 font-mono text-sm tracking-wide text-white" data-testid="liveproof-word">
              {locked ? <span className="italic text-bloq-muted">Sealed — key not on this device yet</span> : show ? word : "•".repeat(Math.min(12, (word || "").length || 8))}
            </span>
            {!locked && (
              <button onClick={() => setShow((s) => !s)} className="text-bloq-muted hover:text-white" title={show ? "Hide" : "Reveal"} data-testid="liveproof-reveal-btn">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            )}
          </div>
          <div className="flex items-center justify-between text-[11px] text-bloq-muted">
            <span>Set by {meta.set_by === userId ? "you" : meta.set_by_name || "them"}</span>
            <span className="flex gap-3">
              <button onClick={() => setEditing(true)} className="font-semibold text-bloq-blue hover:opacity-80" data-testid="liveproof-change-btn">Change</button>
              <button onClick={() => clearMut.mutate()} disabled={clearMut.isPending} className="inline-flex items-center gap-1 text-destructive hover:opacity-80 disabled:opacity-50" data-testid="liveproof-clear-btn"><Trash2 className="h-3 w-3" /> Clear</button>
            </span>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 64))}
            placeholder="e.g. purple giraffe"
            className="h-10 flex-1 rounded-xl border border-white/10 bg-bloq-bg px-3 text-sm outline-none focus:border-bloq-blue/50"
            data-testid="liveproof-input"
          />
          <button
            onClick={() => draft.trim().length >= 3 && setMut.mutate(draft)}
            disabled={draft.trim().length < 3 || setMut.isPending || !sessionId}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-bloq-blue px-3.5 text-sm font-bold text-white hover:bg-bloq-blue/90 disabled:opacity-50"
            data-testid="liveproof-save-btn"
          >
            {setMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Seal
          </button>
          {editing && <button onClick={() => setEditing(false)} className="text-xs text-bloq-muted hover:text-white" data-testid="liveproof-cancel-btn">Cancel</button>}
        </div>
      )}
    </div>
  );
}
